import { Box, Typography, Button, IconButton } from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DriveFolderUpload as DriveFolderUploadIcon,
} from '@mui/icons-material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ColDef } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { TemplateApiModel } from '../../../types/models/template.model';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchTemplates } from '../api/templates.api';
import { useConfirm } from '../contexts/confirm.context';
import { useDeleteTemplate } from '../hooks/api/use-templates.hook';

export const Templates = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();

  const [deleteTemplate, { loading: deletingTemplate, error: deleteTemplateError }] = useDeleteTemplate();
  const columns: ColDef<TemplateApiModel>[] = [
    { field: 'name', filter: true, sortable: true, filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } },
    {
      headerName: 'Actions',
      minWidth: 200,
      maxWidth: 200,
      cellRenderer: ({ api, node: row }: CustomCellRendererProps<TemplateApiModel>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${row.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (row.id) {
                    await deleteTemplate({ templateId: +row.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete Template',
                message: `Are you sure you want to delete template "${row.data?.name}"?`,
                error: deleteTemplateError?.response?.data.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
          <IconButton onClick={() => navigate(`edit/${row.id}/files`)}>
            <DriveFolderUploadIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getTemplates: Parameters<typeof InfniteScrollingGrid<TemplateApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchTemplates({
      cursor,
      limit,
      name: filterQuery?.name,
      sort: sortQuery,
    });
    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title={'Civil 3D - Template Manager'} />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', m: '10px 0' }}>
          <Typography variant="h5">Civil 3D - Template Manager</Typography>
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
            Add Template
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid loading={deletingTemplate} columnDefs={columns} onGetRows={getTemplates} />
        </Box>
      </Box>
    </Box>
  );
};
